import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SectionHeading from "@/components/base/SectionHeading";
import RelatedTitles from "@/components/feature/RelatedTitles";
import HeroCarousel from "./components/HeroCarousel";
import ChapterRail from "./components/ChapterRail";
import SynopsisBlock from "./components/SynopsisBlock";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { heroSlides } from "@/mocks/heroSlides";
import {
  allEpisodes,
  favoriteEpisodes,
  getEpisodeById,
  latestEpisodes,
} from "@/lib/catalog";
import EpisodeRow from "@/components/feature/EpisodeRow";

export default function Home() {
  usePageMeta(
    "朋友的妈妈4 在线观看与剧情介绍 - 朋友的妈妈4影视站",
    "朋友的妈妈4影视站提供最新的朋友的妈妈4电视剧在线观看资源和详细剧情介绍，涵盖多种播放方式，满足影视爱好者的观影需求。",
    "朋友的妈妈4,朋友的妈妈4在线观看,朋友的妈妈4剧情,朋友的妈妈4电视剧,朋友的妈妈4视频,朋友的妈妈4高清",
  );

  const { favoriteIds, toggleFavorite } = useFavorites();
  const collected = favoriteEpisodes(favoriteIds);
  const featured = getEpisodeById("ep-01") ?? allEpisodes[0];

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <HeroCarousel slides={heroSlides} />

      <main className="max-w-[1280px] mx-auto px-4 md:px-8">
        <section className="pt-10 md:pt-14">
          <SectionHeading
            eyebrow="Episode Index"
            title="章节快捷入口"
            description="按集数顺序排列，点击任意卡片直达该集详情与演示播放区。"
            action={
              <Link
                to="/episodes"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md border border-background-300/60 text-xs text-foreground-700 hover:border-primary-500 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
          <ChapterRail episodes={allEpisodes.slice(0, 12)} />
        </section>

        <div className="mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-12">
            <section>
              <SectionHeading
                eyebrow="Latest"
                title="最新收录与更新"
                description="近期上线与刚刚完结的章节，先看这里。"
              />
              <div className="rounded-lg bg-background-100 border border-background-200/70 p-2 md:p-3">
                <div className="flex items-center gap-2 px-2 pt-1 pb-3">
                  <span className="w-2 h-2 rounded-full bg-primary-500" />
                  <p className="text-xs text-foreground-500">
                    全 16 集已完结 · 最近更新时间 2026.09.25
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {latestEpisodes.map((episode, index) => (
                    <EpisodeRow key={episode.id} episode={episode} latest={index < 2} />
                  ))}
                </div>
              </div>
            </section>

            <section>
              <SectionHeading
                eyebrow="Story"
                title="剧情简介与核心导读"
                description="先读简介，再决定从哪一集开始。"
              />
              <SynopsisBlock featured={featured} />

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  {
                    icon: "ri-user-heart-line",
                    title: "人物关系",
                    text: "邻居、旧友、母女与夫妻，四条关系线在同一栋楼里交织。",
                  },
                  {
                    icon: "ri-route-line",
                    title: "叙事节奏",
                    text: "以日常细节推进，每集结尾留一个情绪钩子，适合连续观看。",
                  },
                  {
                    icon: "ri-emotion-line",
                    title: "观看提示",
                    text: "整体克制、偏写实，聚焦中年处境与代际沟通，无夸张桥段。",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg bg-background-100 border border-background-200/70 p-4 hover:border-accent-500/40 transition-colors"
                  >
                    <div className="w-9 h-9 rounded-md bg-accent-100 text-accent-500 flex items-center justify-center">
                      <i className={`${item.icon} text-base`} />
                    </div>
                    <h3 className="mt-3 text-sm font-medium text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-1.5 text-xs leading-6 text-foreground-500">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-lg bg-background-100 border border-background-200/70 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm tracking-wide text-foreground-950 flex items-center gap-2">
                  <i className="ri-heart-3-line text-primary-400" />
                  我的收藏
                </h3>
                <span className="text-[11px] text-foreground-600">
                  {collected.length} 集
                </span>
              </div>

              {collected.length === 0 ? (
                <div className="py-6 text-center">
                  <div className="w-11 h-11 mx-auto rounded-full bg-background-200/70 text-foreground-500 flex items-center justify-center">
                    <i className="ri-heart-line text-lg" />
                  </div>
                  <p className="mt-3 text-xs text-foreground-500 leading-6">
                    还没有收藏的章节
                    <br />
                    在详情页点击收藏按钮即可加入
                  </p>
                  <Link
                    to="/detail/ep-01"
                    className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-background-200/80 hover:bg-background-300/70 text-[11px] text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    去第 1 集看看
                  </Link>
                </div>
              ) : (
                <ul className="space-y-1">
                  {collected.slice(0, 5).map((episode) => (
                    <li key={episode.id} className="flex items-center gap-2">
                      <Link
                        to={`/detail/${episode.id}`}
                        className="flex-1 min-w-0 px-2 py-2 rounded-md hover:bg-background-200/60 transition-colors cursor-pointer"
                      >
                        <span className="block text-[10px] font-label tracking-wider text-accent-500">
                          EP {String(episode.number).padStart(2, "0")}
                        </span>
                        <span className="block text-xs text-foreground-900 truncate">
                          {episode.title}
                        </span>
                      </Link>
                      <button
                        type="button"
                        aria-label="取消收藏"
                        onClick={() => toggleFavorite(episode.id)}
                        className="w-7 h-7 rounded-md text-foreground-500 hover:text-primary-400 hover:bg-background-200/60 transition-colors cursor-pointer flex items-center justify-center"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-3 pt-3 border-t border-background-200/70 text-[11px] leading-5 text-foreground-600">
                收藏仅保存在当前浏览器，无登录、无跨设备同步。
              </p>
            </div>

            <RelatedTitles />

            <div className="rounded-lg bg-background-100 border border-background-200/70 p-4">
              <h3 className="font-heading text-sm tracking-wide text-foreground-950 mb-3">
                剧集速览
              </h3>
              <ul className="space-y-2.5 text-xs">
                {[
                  { icon: "ri-film-line", label: "总集数", value: "16 集" },
                  { icon: "ri-calendar-line", label: "首播日期", value: "2026.06.12" },
                  { icon: "ri-time-line", label: "单集时长", value: "约 42 分钟" },
                  { icon: "ri-checkbox-circle-line", label: "更新状态", value: "已完结" },
                  { icon: "ri-play-circle-line", label: "播放方式", value: "演示播放器" },
                ].map((item) => (
                  <li key={item.label} className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 text-foreground-600">
                      <i className={`${item.icon} text-accent-500`} />
                      {item.label}
                    </span>
                    <span className="text-foreground-900">{item.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}